import { useState, useCallback, useEffect, useMemo } from 'react';
import { apiService } from '../../services';
import { Article, Event, Album, Book, Canticle } from '../../types';

type SelectedType = 'articles' | 'events' | 'albums' | 'canticles' | 'books';
type Entity = Article | Event | Album | Book | Canticle;

// Must match FilterBar's Filters interface exactly
interface Filters {
  title?: string;
  publishDateFrom?: string;
  publishDateTo?: string;
  status?: string;
}

interface Pagination {
  currentPage: number;
  itemsPerPage: number;
  total: number;
}

interface UseDashboardProps {
  selectedType: SelectedType;
  editingAlbumId?: number | null;
  onSuccess?: () => void;
}

export function useDashboard({ selectedType, editingAlbumId, onSuccess }: UseDashboardProps) {
  // Raw items fetched from API (unfiltered)
  const [allItems, setAllItems] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    itemsPerPage: 20,
    total: 0,
  });

  // Album editor state (only used when selectedType === 'albums')
  const [mediaItems, setMediaItems] = useState<any[]>([]);
  const [albumLoading, setAlbumLoading] = useState(false);
  const [albumError, setAlbumError] = useState<string | null>(null);

  // Fetch ALL items from server (filtering + pagination happen client-side)
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      switch (selectedType) {
        case 'articles': {
          const response = await apiService.getArticles(0, 1000, false);
          setAllItems(response.articles);
          break;
        }
        case 'events': {
          const response = await apiService.getEvents(0, 1000, false);
          setAllItems(response.events);
          break;
        }
        case 'albums': {
          const response = await apiService.getAlbums();
          setAllItems(response.albums);
          break;
        }
        case 'books': {
          const books = await apiService.getBooks(0, 1000, false);
          setAllItems(books);
          break;
        }
        case 'canticles': {
          const canticles = await apiService.getCanticles(0, 1000, false);
          setAllItems(canticles);
          break;
        }
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedType]);

  // Client-side filtering
  const filteredItems = useMemo(() => {
    let result = allItems;

    if (filters.title) {
      const search = filters.title.toLowerCase();
      result = result.filter((item) =>
        (item as any).title?.toLowerCase().includes(search)
      );
    }

    if (filters.status) {
      result = result.filter((item) => (item as any).status === filters.status);
    }

    if (filters.publishDateFrom) {
      const from = new Date(filters.publishDateFrom);
      result = result.filter((item) => {
        const date = (item as any).published_at || (item as any).created_at;
        return date ? new Date(date) >= from : false;
      });
    }

    if (filters.publishDateTo) {
      const to = new Date(filters.publishDateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter((item) => {
        const date = (item as any).published_at || (item as any).created_at;
        return date ? new Date(date) <= to : false;
      });
    }

    return result;
  }, [allItems, filters]);

  // Client-side pagination of filtered results
  const items = useMemo(() => {
    const start = (pagination.currentPage - 1) * pagination.itemsPerPage;
    return filteredItems.slice(start, start + pagination.itemsPerPage);
  }, [filteredItems, pagination.currentPage, pagination.itemsPerPage]);

  // Sync total with filtered count (no page reset here — updateFilters handles it)
  useEffect(() => {
    setPagination((prev) => ({ ...prev, total: filteredItems.length }));
  }, [filteredItems.length]);

  // Fetch album media (only for albums)
  const fetchAlbumMedia = useCallback(async () => {
    if (selectedType !== 'albums' || !editingAlbumId) {
      setMediaItems([]);
      return;
    }

    setAlbumLoading(true);
    setAlbumError(null);
    try {
      const album = await apiService.getAlbum(editingAlbumId);
      setMediaItems(album.media_items || []);
    } catch (error: any) {
      setAlbumError(error.message || 'Error loading album media');
    } finally {
      setAlbumLoading(false);
    }
  }, [selectedType, editingAlbumId]);

  // Load list on mount and when dependencies change
  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Load album media when editing an album
  useEffect(() => {
    fetchAlbumMedia();
  }, [fetchAlbumMedia]);

  // Filter actions
  const updateFilters = useCallback((newFilters: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  // Pagination actions
  const goToPage = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  }, []);

  // Album actions
  const handleAddMedia = useCallback(
    async (files: FileList) => {
      if (!editingAlbumId || !files || files.length === 0) return;

      setAlbumLoading(true);
      setAlbumError(null);
      try {
        // Upload each file individually
        for (let i = 0; i < files.length; i++) {
          await apiService.uploadAlbumMedia(editingAlbumId, files[i]);
        }
        await fetchAlbumMedia();
        onSuccess?.();
      } catch (error: any) {
        setAlbumError(error.message || 'Error adding media');
        throw error;
      } finally {
        setAlbumLoading(false);
      }
    },
    [editingAlbumId, fetchAlbumMedia, onSuccess]
  );

  const handleDeleteMedia = useCallback(
    async (mediaId: number) => {
      if (!editingAlbumId) return;

      setAlbumLoading(true);
      setAlbumError(null);
      try {
        await apiService.deleteAlbumMedia(mediaId);
        await fetchAlbumMedia();
        onSuccess?.();
      } catch (error: any) {
        setAlbumError(error.message || 'Error deleting media');
        throw error;
      } finally {
        setAlbumLoading(false);
      }
    },
    [editingAlbumId, fetchAlbumMedia, onSuccess]
  );

  const handleUpdateAlbum = useCallback(
    async (albumData: Partial<Album>) => {
      if (!editingAlbumId) return;

      setAlbumLoading(true);
      setAlbumError(null);
      try {
        await apiService.updateAlbum(editingAlbumId, albumData);
        await Promise.all([fetchList(), fetchAlbumMedia()]);
        onSuccess?.();
      } catch (error: any) {
        setAlbumError(error.message || 'Error updating album');
        throw error;
      } finally {
        setAlbumLoading(false);
      }
    },
    [editingAlbumId, fetchList, fetchAlbumMedia, onSuccess]
  );

  return {
    // Entity list
    items,
    loading,
    filters,
    pagination,
    updateFilters,
    clearFilters,
    goToPage,
    refresh: fetchList,

    // Album editor (only relevant when selectedType === 'albums')
    mediaItems,
    albumLoading,
    albumError,
    handleAddMedia,
    handleDeleteMedia,
    handleUpdateAlbum,
    setAlbumError,
  };
}
